import * as React from 'react'
import dayjs from 'dayjs'
import {
  TableRow,
  TableCell,
  Box,
  Tooltip,
  IconButton,
  TextField,
  MenuItem,
  Select,
  Typography,
  InputAdornment
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SaveIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { getOriginalCode } from 'utils/bin'
import MiniAuto from './MiniAuto'
import { BinKind } from 'constants/index'

const COL_WIDTH = {
  type: 90,
  binCode: 150,
  codes: 260,
  updated: 150,
  action: 140
}
const rowHeight = 34

type Props = {
  binID: string
  binRows: any[]
  editProductCodes: string[]
  newRows: string[]
  updating: boolean
  productCodes: string[]
  binCodes: string[]

  editingBinCode: string
  setEditingBinCode: (v: string) => void

  editingType: BinKind
  setEditingType: React.Dispatch<React.SetStateAction<BinKind>>

  onDeleteProduct: (idx: number) => void
  onAddRow: () => void
  onCancel: () => void
  onOpenTransfer: (idx: number, anchor: HTMLElement) => void
  onSaveAll: () => void
  setEditProductCodes: React.Dispatch<React.SetStateAction<string[]>>
  setNewRows: React.Dispatch<React.SetStateAction<string[]>>
  onDeleteBin: (binID: string) => void
}

const BinEditRow: React.FC<Props> = ({
  binID,
  binRows,
  editProductCodes,
  newRows,
  updating,
  productCodes,
  editingBinCode,
  setEditingBinCode,
  editingType,
  setEditingType,
  onDeleteProduct,
  onAddRow,
  onCancel,
  onOpenTransfer,
  onSaveAll,
  setEditProductCodes,
  setNewRows,
  onDeleteBin
}) => {
  const originalBinCode = binRows?.[0]?.binCode ?? ''
  const originalCodeAt = (idx: number) => getOriginalCode(binRows, idx)

  const capsuleSx = {
    px: 0.75,
    py: 0.25,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 12,
    lineHeight: '18px',
    borderRadius: '10px',
    bgcolor: '#eef6ff',
    border: '1px solid #bcd8ff',
    color: '#2a4a7b',
    maxWidth: 70,
    overflow: 'hidden',
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
    fontWeight: 700
  }

  const rowSpanCount = Math.max(editProductCodes.length + newRows.length, 1)

  return (
    <>
      {editProductCodes.map((code, idx) => (
        <TableRow
          key={binID + '-edit-' + idx}
          sx={{
            backgroundColor: '#bfdbfe',
            '& td': { borderColor: '#93c5fd' },
            height: rowHeight
          }}
        >
          {idx === 0 && (
            <TableCell
              align='center'
              rowSpan={rowSpanCount}
              sx={{
                width: COL_WIDTH.type,
                minWidth: COL_WIDTH.type,
                fontWeight: 700,
                border: '1px solid #e0e0e0',
                fontSize: 13,
                height: rowHeight,
                p: 0
              }}
            >
              <Select
                value={editingType}
                size='small'
                onChange={e => {
                  const next = e.target.value as BinKind
                  setEditingType(next)

                  if (next === BinKind.INVENTORY) {
                    setEditProductCodes([''])
                    setNewRows([])
                  }
                }}
                sx={{
                  fontSize: 13,
                  height: 32,
                  minWidth: 150
                }}
              >
                <MenuItem value={BinKind.PICK_UP}>PICK UP</MenuItem>
                <MenuItem value={BinKind.INVENTORY}>INVENTORY</MenuItem>
                <MenuItem value={BinKind.CART}>CART</MenuItem>
                <MenuItem value={BinKind.AISLE}>AISLE</MenuItem>
              </Select>
            </TableCell>
          )}

          {idx === 0 && (
            <TableCell
              align='center'
              rowSpan={rowSpanCount}
              sx={{
                width: COL_WIDTH.binCode,
                minWidth: COL_WIDTH.binCode,
                fontWeight: 700,
                border: '1px solid #e0e0e0',
                fontSize: 13,
                height: rowHeight,
                p: 0
              }}
            >
              <TextField
                value={editingBinCode}
                onChange={e => setEditingBinCode(e.target.value)}
                size='small'
                fullWidth
                placeholder='Bin Code'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' sx={{ ml: 0.5 }}>
                      <Tooltip title='Original bin code'>
                        <Typography sx={capsuleSx}>
                          {originalBinCode || '--'}
                        </Typography>
                      </Tooltip>
                      <ArrowRightAltIcon
                        fontSize='small'
                        sx={{ ml: 0.5, opacity: 0.6 }}
                      />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  style: { height: 32, padding: '0 8px', fontSize: 13 }
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 32,
                    fontSize: 13,
                    background: 'transparent',
                    p: 0
                  }
                }}
              />
            </TableCell>
          )}

          <TableCell
            align='center'
            sx={{
              border: '1px solid #e0e0e0',
              width: COL_WIDTH.codes,
              minWidth: COL_WIDTH.codes,
              p: 0,
              height: rowHeight
            }}
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              sx={{ height: rowHeight, px: 1 }}
              gap={0.5}
            >
              <Tooltip title='Original product code'>
                <Typography sx={capsuleSx}>
                  {originalCodeAt(idx) || '--'}
                </Typography>
              </Tooltip>

              <ArrowRightAltIcon fontSize='small' sx={{ opacity: 0.6 }} />

              <MiniAuto
                value={code}
                onChange={v => {
                  const copy = [...editProductCodes]
                  copy[idx] = v
                  setEditProductCodes(copy)
                }}
                options={productCodes}
                freeSolo={false}
                width={150}
              />

              <Tooltip title='Transfer'>
                <span>
                  <IconButton
                    size='small'
                    color='info'
                    sx={{ ml: 0.25, height: 32, width: 32, p: 0 }}
                    onClick={e => onOpenTransfer(idx, e.currentTarget)}
                    aria-label='transfer product'
                  >
                    <MoveDownIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title='Delete'>
                <span>
                  <IconButton
                    color='error'
                    size='small'
                    sx={{ ml: 0.25, height: 32, width: 32, p: 0 }}
                    onClick={() => onDeleteProduct(idx)}
                    disabled={editProductCodes.length <= 1}
                    aria-label='delete product'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </TableCell>

          <TableCell
            align='center'
            sx={{
              border: '1px solid #e0e0e0',
              width: COL_WIDTH.updated,
              minWidth: COL_WIDTH.updated,
              height: rowHeight,
              p: 0
            }}
          >
            {binRows[idx]?.updatedAt
              ? dayjs(binRows[idx].updatedAt).format('YYYY-MM-DD HH:mm')
              : '--'}
          </TableCell>

          {idx === 0 && (
            <TableCell
              align='center'
              rowSpan={rowSpanCount}
              sx={{
                border: '1px solid #e0e0e0',
                width: COL_WIDTH.action,
                minWidth: COL_WIDTH.action,
                verticalAlign: 'middle',
                height: rowHeight,
                p: 0
              }}
            >
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                gap={1}
                height={rowHeight * rowSpanCount}
              >
                <Box display='flex' gap={1}>
                  <Tooltip title='Save'>
                    <span>
                      <IconButton
                        color='success'
                        size='small'
                        sx={{ height: 32, width: 32, p: 0 }}
                        onClick={onSaveAll}
                        disabled={updating}
                        aria-label='save all'
                      >
                        <SaveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Cancel'>
                    <span>
                      <IconButton
                        color='secondary'
                        size='small'
                        sx={{ height: 32, width: 32, p: 0 }}
                        onClick={onCancel}
                        aria-label='cancel edit'
                      >
                        <CancelIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Add Product'>
                    <span>
                      <IconButton
                        color='primary'
                        size='small'
                        sx={{ height: 32, width: 32, p: 0 }}
                        onClick={onAddRow}
                        aria-label='add product'
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Delete Bin'>
                    <span>
                      <IconButton
                        color='error'
                        size='small'
                        sx={{ height: 32, width: 32, p: 0 }}
                        onClick={() => onDeleteBin(binID)}
                        aria-label='delete bin'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))}

      {newRows.map((val, nIdx) => (
        <TableRow
          key={`${binID}-new-${nIdx}`}
          sx={{ backgroundColor: '#eafce8', height: rowHeight }}
        >
          <TableCell
            align='center'
            sx={{
              border: '1px solid #e0e0e0',
              width: COL_WIDTH.codes,
              minWidth: COL_WIDTH.codes,
              height: rowHeight,
              p: 0
            }}
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              sx={{ height: rowHeight, px: 1 }}
              gap={0.5}
            >
              <Tooltip title='Original product code'>
                <Typography sx={capsuleSx}>--</Typography>
              </Tooltip>

              <ArrowRightAltIcon fontSize='small' sx={{ opacity: 0.6 }} />

              <MiniAuto
                value={val}
                onChange={v => {
                  const next = [...newRows]
                  next[nIdx] = v
                  setNewRows(next)
                }}
                options={productCodes}
                freeSolo={false}
                width={150}
              />

              <Tooltip title='Delete'>
                <span>
                  <IconButton
                    color='error'
                    size='small'
                    sx={{ ml: 0.25, height: 32, width: 32, p: 0 }}
                    onClick={() => {
                      const next = [...newRows]
                      next.splice(nIdx, 1)
                      setNewRows(next)
                    }}
                    aria-label='delete new product'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </TableCell>

          <TableCell
            align='center'
            sx={{
              border: '1px solid #e0e0e0',
              width: COL_WIDTH.updated,
              minWidth: COL_WIDTH.updated,
              height: rowHeight,
              p: 0
            }}
          />
        </TableRow>
      ))}
    </>
  )
}

export default React.memo(BinEditRow)
